import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  updateTripMeta,
  prepareTripExport,
  saveTripBudgets,
  addTripExpense,
  removeTripExpense,
  markTripExported,
  resetTripMoney,
  subscribeToExpenseCategories,
  saveExpenseCategories,
  DEFAULT_EXPENSE_CATEGORIES,
} from '../services/firebase';
import {
  localDateStr,
  localDateTimeStr,
  formatYen,
  getTrip,
  getBudgets,
  getExpenses,
  tripDisplayName,
  hasBudgetPlan,
  calcOverall,
  barState,
  barPct,
  mergeBudgetRows,
  sumExpensesByCat,
  normalizeColor,
  expensesOfDay,
  buildExportPayload,
  CALC_INIT,
  applyCalcKey,
  calcValue,
  calcExprText,
  getSlotView,
  slotHasData,
  slotLabel,
  slotStatus,
  listSlotKeys,
  sortSlotKeys,
  nextFreeSlotKey,
  tripTheme,
  themeLines,
  normalizeThemeText,
  moveItem,
} from '../utils/tripLogic';
import MeetupBudgetCard from './MeetupBudgetCard';
import DateRangeCalendar from './DateRangeCalendar';

// 旅行費用画面（旅行リストページ内の全画面サブビュー）
// タブなしのガイド型1画面構成（社長指示 2026-07-07）：
//   最上段＝予実差サマリー（常時固定）
//   状態A（予算未決定）＝「💰 予算を決める」だけを表示 → 予算設定パネル
//   状態B（予算決定後）＝費目行（比較＋実費記録＋予算修正）・完了ボタンを1画面に集約
// 実費記録（v1.9.2・社長指示）：費目行タップ→ポップアップ電卓（＋−×÷＝つき）→記録する
//   常設テンキーは廃止（1画面をスッキリさせる）。行タップ起点に一本化
// 記録後リセット（v1.9.2）：エクスポート成功時に費用データのリセットを確認
//   （持ち物リストはそのまま）。⚙️旅行の設定からも手動リセット可能
// 保存先は checklists ドキュメントの任意フィールド（trip / budgets / expenses）
// プラン枠（v1.10.0）：ヘッダー直下のチップで最大3プランを切り替え。
//   枠1＝ルートフィールド（既存データそのまま）／枠2・3＝tripSlots.slot2/slot3。
//   このコンポーネントは view（getSlotView）経由でしか枠データを読まない＝枠を跨いで混ざらない
// テーマ（v1.11.0）：メインテーマ（旅行の目的・目立つ色）とサブテーマ（過ごし方の方針・控えめ）。
//   旅行中に常に目に入るよう、プランチップ直下＝画面最上部にカード表示。タップで編集モーダル。
//   保存先は trip.mainTheme / trip.subTheme（複数行テキスト・1行=1項目）

// 費目マスタ用アイコンのプリセット
const EXPENSE_ICONS = ['🚃', '🏨', '🍜', '🎡', '🎁', '📦', '✈️', '🚗', '⛽', '☕', '🍺', '🎫', '💊', '🛍️'];

// 費目の色分け（社長指示 2026-07-08）：赤＝事業寄り／青＝private寄り／白＝両方（中立・無色）
const COLOR_CHOICES = [
  { value: 'red', label: '赤（事業寄り）' },
  { value: 'blue', label: '青（private寄り）' },
  { value: 'white', label: '白（両方）' },
];

// 電卓キーの配置（4列グリッド。'back'=1文字削除・'clear'=全消し）
// レシート2枚を「1200＋800＝」でまとめて入れられるよう四則演算つき
const CALC_KEYS = [
  '7', '8', '9', '/',
  '4', '5', '6', '*',
  '1', '2', '3', '-',
  '0', '00', 'back', '+',
];
const CALC_KEY_LABEL = { '/': '÷', '*': '×', '-': '−', '+': '＋', back: '⌫', clear: 'C' };

function genId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

function vibrate(ms) {
  if (navigator.vibrate) navigator.vibrate(ms);
}

export default function TripMoneyView({ uid, set, onBack, slotKey = 'slot1', onSlotChange }) {
  // 費目マスタ（settings/expenseCategories。未作成なら初期6費目）
  const [master, setMaster] = useState(DEFAULT_EXPENSE_CATEGORIES);
  useEffect(() => {
    if (!uid) return undefined;
    const unsub = subscribeToExpenseCategories(uid, setMaster);
    return () => unsub && unsub();
  }, [uid]);

  // 予算設定パネル（状態Aの「💰 予算を決める」から開く）
  const [setupOpen, setSetupOpen] = useState(false);
  // 「スタート」直後、Firestore 反映待ちの間も状態Bを見せるための楽観フラグ
  const [optimisticPlanned, setOptimisticPlanned] = useState(false);

  // ポップアップ電卓（費目行タップで開く）：対象費目・電卓状態・メモ
  const [calcCatId, setCalcCatId] = useState(null);
  const [calcState, setCalcState] = useState(CALC_INIT);
  const [calcMemo, setCalcMemo] = useState('');

  // 費用データリセットの確認モーダル：null | { afterExport: boolean }
  const [resetAsk, setResetAsk] = useState(null);
  const [resetting, setResetting] = useState(false);

  // ✏️で開く費目パネル（予算修正＋明細）
  const [editCatId, setEditCatId] = useState(null);

  // 旅行の設定（区分・日付・費目マスタ）の折りたたみ
  const [settingsOpen, setSettingsOpen] = useState(false);

  // 期間カレンダーの開閉（v1.12.3）
  const [calendarOpen, setCalendarOpen] = useState(false);

  // 予算金額入力のドラフト（onBlur で保存）
  const [budgetDraft, setBudgetDraft] = useState({});

  // 旅行名のドラフト（onBlur で保存。null＝編集中でない。空文字は「消して未設定に戻す」編集中の状態）
  const [tripNameDraft, setTripNameDraft] = useState(null);

  // テーマ編集モーダル（v1.11.0）：null | { main: string, sub: string }（編集中ドラフト）
  const [themeDraft, setThemeDraft] = useState(null);

  // 費目マスタ編集モーダル
  const [masterModal, setMasterModal] = useState(false);
  const [catForm, setCatForm] = useState({ id: null, name: '', icon: EXPENSE_ICONS[0], color: 'white' });
  const [catError, setCatError] = useState('');

  // エクスポート確認モーダル
  const [exportModal, setExportModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  // トースト（保存直後のフィードバック・3秒）
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const showToast = useCallback((text) => {
    setToast(text);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  }, []);
  useEffect(() => () => toastTimer.current && clearTimeout(toastTimer.current), []);

  // 選択中プラン枠のビュー（枠1＝ルートフィールド／枠2・3＝tripSlots）。
  // 以降の読み取りはすべてこの view を通す（枠を跨いでデータが混ざらない）
  const view = useMemo(() => getSlotView(set, slotKey), [set, slotKey]);

  // プラン枠やセットが切り替わったら、編集途中のローカルUI状態を全部リセットする
  // （前の枠のドラフト・電卓・モーダルを持ち越さない＝枠間の混入防止）
  useEffect(() => {
    setOptimisticPlanned(false);
    setSetupOpen(false);
    setCalcCatId(null);
    setCalcState(CALC_INIT);
    setCalcMemo('');
    setResetAsk(null);
    setEditCatId(null);
    setBudgetDraft({});
    setTripNameDraft(null);
    setThemeDraft(null);
    setExportModal(false);
    setExportError('');
  }, [set.id, slotKey]);

  const trip = getTrip(view);
  const budgets = getBudgets(view);
  const expenses = getExpenses(view);
  const overall = useMemo(() => calcOverall(view), [view]);
  const budgetRows = useMemo(() => mergeBudgetRows(master, budgets), [master, budgets]);
  const today = localDateStr();
  const todayExpenses = useMemo(() => expensesOfDay(expenses, today), [expenses, today]);

  // チップに出す枠＝データのある枠＋選択中の枠（作りたての空枠も1つだけ見える）。旅行日が近い順（v1.15.0）
  const slotKeys = useMemo(() => {
    const keys = listSlotKeys(set);
    if (!keys.includes(slotKey)) keys.push(slotKey);
    return sortSlotKeys(set, keys, today);
  }, [set, slotKey, today]);

  // ➕：最初の空き枠を新しいプランとして選び、予算設定パネルを開く。
  // 選択中の枠がまだ空なら、それがそのまま「新しいプラン」なので枠は増やさない
  const pendingSetupRef = useRef(false);
  const handleAddPlan = () => {
    if (!slotHasData(set, slotKey)) {
      pendingSetupRef.current = false;
      setSetupOpen(true);
      return;
    }
    pendingSetupRef.current = true;
    if (onSlotChange) onSlotChange(nextFreeSlotKey(set));
  };
  useEffect(() => {
    if (pendingSetupRef.current) {
      pendingSetupRef.current = false;
      setSetupOpen(true);
    }
  }, [slotKey]);

  // 費目行（表示用）：予算スナップショット∪マスタ＋実費・残額。
  // budgets にもマスタにも無い catId の実費（マスタ変更後の孤児）は末尾に足す
  const rows = useMemo(() => {
    const actualByCat = sumExpensesByCat(expenses);
    const seen = new Set(budgetRows.map((r) => r.catId));
    const out = budgetRows.map((r) => ({
      ...r,
      budget: r.amount,
      actual: actualByCat[r.catId] || 0,
      remaining: r.amount - (actualByCat[r.catId] || 0),
    }));
    Object.keys(actualByCat).forEach((catId) => {
      if (seen.has(catId)) return;
      out.push({
        catId,
        name: '不明な費目',
        icon: '❓',
        color: normalizeColor(null),
        amount: 0,
        budget: 0,
        actual: actualByCat[catId],
        remaining: -actualByCat[catId],
      });
    });
    return out;
  }, [budgetRows, expenses]);

  // 予算決定済みか（状態A/Bの分岐）
  const planReady = hasBudgetPlan(view) || optimisticPlanned;

  // 予算入力ドラフトを反映した合計（設定パネルの「スタート」判定用）
  const draftTotal = useMemo(
    () =>
      budgetRows.reduce((n, r) => {
        const d = budgetDraft[r.catId];
        return n + (d !== undefined ? parseInt(d, 10) || 0 : r.amount);
      }, 0),
    [budgetRows, budgetDraft]
  );

  // 電卓モーダルの対象費目行（費目が消えたら null → モーダルは描画されない）
  const calcRow = calcCatId ? rows.find((r) => r.catId === calcCatId) || null : null;
  const calcAmount = calcValue(calcState);

  // 保存済みの予定だけを1.5秒まとめて同期。入力途中のドラフト/実費の追加では送らない。
  const [planSyncError, setPlanSyncError] = useState('');
  const [planSyncRetry, setPlanSyncRetry] = useState(0);
  const syncedPlans = useRef(new Map());
  const planSyncSource = JSON.stringify(listSlotKeys(set).map(key => {
    const value = getSlotView(set, key);
    return { key, id: value.id, name: value.name,
      trip: value.trip ? { recordId: value.trip.recordId, kind: value.trip.kind, startDate: value.trip.startDate,
        endDate: value.trip.endDate, tripName: value.trip.tripName, status: value.trip.status } : null,
      budgets: value.budgets || [] };
  }));
  useEffect(() => {
    if (!uid) return undefined;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      const plans = JSON.parse(planSyncSource).filter(p => p.trip?.kind === 'business'
        && p.trip.startDate && p.trip.status !== 'done' && p.budgets.length > 0);
      for (const plan of plans) {
        const fingerprint = JSON.stringify(plan);
        if (cancelled) return;
        if (syncedPlans.current.get(plan.id) === fingerprint) continue;
        try {
          const saved = await prepareTripExport(uid, set.id, plan.key);
          if (cancelled) return;
          const payload = { ...buildExportPayload(saved, master, localDateStr()), mode: 'planned' };
          const response = await fetch('/api/export-trip', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
          });
          const jsonResponse = response.headers.get('content-type')?.includes('application/json');
          const result = jsonResponse ? await response.json() : null;
          if (!response.ok || !result?.ok) throw new Error(result?.error || '接続を確認して再試行してください。');
          syncedPlans.current.set(plan.id, fingerprint);
          window.dispatchEvent(new Event('taskflow:budget-updated'));
          if (!cancelled) setPlanSyncError('');
        } catch (error) {
          if (!cancelled) setPlanSyncError('予定予算をスプシに反映できませんでした。' + error.message);
          return;
        }
      }
    }, 1500);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [uid, set.id, planSyncSource, master, planSyncRetry]);

  // ===== trip メタの書き込み（変えたフィールドだけ部分更新・選択中の枠へ／v1.15.1） =====
  // 画面状態（view）を土台に trip 全体を組み立てて上書きしない。
  // スマホでは「旅行名の onBlur 保存」と「区分ボタンの click」が同じタップ処理内で連続し、
  // 再描画前の古い view を土台にすると直前に保存した旅行名を消してしまうため。
  // 枠がまだ空（trip 無し）のときだけ区分・状態の既定値を同梱する（null の既定値は同梱しない）
  const writeTripMeta = useCallback(
    (patch) => {
      const fields = getTrip(view) ? patch : { kind: 'private', status: 'active', ...patch };
      updateTripMeta(uid, set.id, fields, slotKey).catch((err) => {
        console.error('Trip meta write failed:', err);
      });
    },
    [uid, set.id, view, slotKey]
  );

  // ===== 旅行日（v1.12.3）：期間カレンダーで開始→終了をまとめて選ぶ =====
  // 開始日より前の終了日は pickRange 側で作られないため、逆転チェックは不要になった。
  const handleRangeChange = useCallback(
    ({ startDate, endDate }) => {
      writeTripMeta({ startDate: startDate || null, endDate: endDate || null });
    },
    [writeTripMeta]
  );

  // ===== テーマ（v1.11.0）：メイン＝旅行の目的／サブ＝過ごし方の方針 =====
  const mainThemeLines = themeLines(tripTheme(view, 'mainTheme'));
  const subThemeLines = themeLines(tripTheme(view, 'subTheme'));
  const hasTheme = mainThemeLines.length > 0 || subThemeLines.length > 0;

  const openThemeModal = () => {
    setThemeDraft({
      main: tripTheme(view, 'mainTheme'),
      sub: tripTheme(view, 'subTheme'),
    });
  };

  const saveThemes = () => {
    if (!themeDraft) return;
    writeTripMeta({
      mainTheme: normalizeThemeText(themeDraft.main),
      subTheme: normalizeThemeText(themeDraft.sub),
    });
    setThemeDraft(null);
    showToast('🎯 テーマを保存しました');
    vibrate(30);
  };

  // 旅行名の確定（onBlur / Enter）。空白のみなら null＝未設定（セット名フォールバック）に戻す
  const commitTripName = () => {
    if (tripNameDraft === null) return;
    const trimmed = tripNameDraft.trim();
    const current = (trip && trip.tripName) || null;
    setTripNameDraft(null);
    if ((trimmed || null) === current) return; // 変更なしなら書き込まない
    writeTripMeta({ tripName: trimmed || null });
  };

  // ===== 実費の記録（電卓モーダルの「記録する」から） =====
  const saveExpense = (row, amount, memoText) => {
    const expense = {
      id: genId(),
      catId: row.catId,
      amount,
      memo: (memoText || '').trim(),
      at: localDateTimeStr(),
    };
    addTripExpense(uid, set.id, expense, slotKey).catch((err) => {
      console.error('Expense add failed:', err);
    });
    // 残額は楽観的に計算（onSnapshot 反映を待たない）
    const remaining = row.budget - (row.actual + amount);
    const remainText = row.budget > 0 ? `（残 ${formatYen(remaining)}）` : '';
    showToast(`${row.icon} ${row.name} ${formatYen(amount)} を記録しました${remainText}`);
    vibrate(30);
  };

  // 費目行タップ：その費目のポップアップ電卓を開く
  const handleRowTap = (row) => {
    setCalcState(CALC_INIT);
    setCalcMemo('');
    setCalcCatId(row.catId);
  };

  const closeCalc = () => {
    setCalcCatId(null);
    setCalcState(CALC_INIT);
    setCalcMemo('');
  };

  const handleCalcRecord = () => {
    if (!calcRow || calcAmount <= 0) return;
    saveExpense(calcRow, calcAmount, calcMemo);
    closeCalc();
  };

  // ===== 費用データのリセット（選択中の枠の trip / budgets / expenses を削除。持ち物はそのまま） =====
  const handleResetExecute = async () => {
    setResetting(true);
    const deletePlan = !!(resetAsk && resetAsk.deletePlan);
    try {
      if (trip?.recordId && trip.kind === 'business' && trip.status !== 'done' && trip.startDate) {
        const payload = { ...buildExportPayload(view, master, localDateStr()), mode: 'cancelled' };
        const response = await fetch('/api/export-trip', { method: 'POST',
          headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        const result = response.headers.get('content-type')?.includes('application/json') ? await response.json() : null;
        if (!response.ok || !result?.ok) throw new Error('予定の取消状態を保存できませんでした。再試行してください。');
      }
      await resetTripMoney(uid, set.id, slotKey);
      window.dispatchEvent(new Event('taskflow:budget-updated'));
      setResetAsk(null);
      setOptimisticPlanned(false);
      setSettingsOpen(false);
      closeCalc();
      if (deletePlan) {
        // 消した枠の次に日付が近いプランへ移る（残りが無ければ枠1）
        const rest = slotKeys.filter((k) => k !== slotKey);
        if (onSlotChange) onSlotChange(rest[0] || 'slot1');
        showToast('🗑️ プランを削除しました（持ち物リストはそのまま）');
      } else {
        showToast('🗑️ 費用データをリセットしました（持ち物リストはそのまま）');
      }
    } catch (err) {
      console.error('Trip money reset failed:', err);
      showToast('リセットに失敗しました。通信状態を確認してください');
    } finally {
      setResetting(false);
    }
  };

  const handleRemoveExpense = (expense) => {
    removeTripExpense(uid, set.id, expense, slotKey).catch((err) => {
      console.error('Expense remove failed:', err);
    });
    showToast(`${formatYen(expense.amount)} を取り消しました`);
  };

  // ===== 予算の保存（onBlur / Enter） =====
  const commitBudget = (catId) => {
    const draft = budgetDraft[catId];
    if (draft === undefined) return;
    const amount = Math.max(0, parseInt(draft, 10) || 0);
    const next = budgetRows.map((r) => (r.catId === catId ? { ...r, amount } : r));
    setBudgetDraft((prev) => {
      const n = { ...prev };
      delete n[catId];
      return n;
    });
    saveTripBudgets(uid, set.id, next, slotKey).catch((err) => {
      console.error('Budgets write failed:', err);
    });
  };

  // 設定パネルの「この予算でスタート」：未確定ドラフトをまとめて保存して状態Bへ
  const handleStartWithBudget = () => {
    const next = budgetRows.map((r) => {
      const d = budgetDraft[r.catId];
      return d !== undefined ? { ...r, amount: Math.max(0, parseInt(d, 10) || 0) } : r;
    });
    setBudgetDraft({});
    saveTripBudgets(uid, set.id, next, slotKey).catch((err) => {
      console.error('Budgets write failed:', err);
    });
    setOptimisticPlanned(true);
    setSetupOpen(false);
    showToast('💰 予算を決めました。費目の行をタップして実費を記録できます');
    vibrate(30);
  };

  // ===== 費目マスタ編集 =====
  const openMasterModal = () => {
    setCatForm({ id: null, name: '', icon: EXPENSE_ICONS[0], color: 'white' });
    setCatError('');
    setMasterModal(true);
  };

  const handleCatSubmit = (e) => {
    e.preventDefault();
    const name = catForm.name.trim();
    if (!name) return;
    if (master.some((c) => c.name === name && c.id !== catForm.id)) {
      setCatError('同じ名前の費目があります');
      return;
    }
    setCatError('');
    let next;
    if (catForm.id) {
      next = master.map((c) => (c.id === catForm.id ? { ...c, name, icon: catForm.icon, color: catForm.color } : c));
    } else {
      next = [...master, { id: genId(), name, icon: catForm.icon, color: catForm.color }];
    }
    setMaster(next); // 楽観的更新
    saveExpenseCategories(uid, next).catch((err) => {
      console.error('Categories write failed:', err);
    });
    setCatForm({ id: null, name: '', icon: EXPENSE_ICONS[0], color: 'white' });
  };

  // 費目の並べ替え（v1.13.0・社長指示 2026-08-07）：マスタ配列の並び＝表示順。
  // 操作は費目マスタの編集モーダル内だけに置き、結果は予算・実費・比較・スプシに反映される。
  // マスタは全プラン共通の1ドキュメントなので、並び順も全プラン共通で効く。
  const handleCatMove = (index, dir) => {
    const next = moveItem(master, index, dir);
    if (next.length !== master.length) return;
    if (next.every((c, i) => c.id === master[i].id)) return; // 端で動かない＝書き込まない
    setMaster(next); // 楽観的更新（onSnapshot が追認）
    saveExpenseCategories(uid, next).catch((err) => {
      console.error('Categories order write failed:', err);
    });
    vibrate(15);
  };

  const handleCatDelete = (cat) => {
    if (!window.confirm(`費目「${cat.name}」をマスタから削除しますか？\n（過去の旅行の記録は消えません）`)) return;
    const next = master.filter((c) => c.id !== cat.id);
    setMaster(next);
    saveExpenseCategories(uid, next).catch((err) => {
      console.error('Categories write failed:', err);
    });
    if (catForm.id === cat.id) setCatForm({ id: null, name: '', icon: EXPENSE_ICONS[0], color: 'white' });
  };

  // ===== エクスポート（Cloudflare Pages Functions 経由でスプシへ） =====
  const handleExport = async () => {
    setExporting(true);
    setExportError('');
    try {
      // view.id ＝枠ごとの冪等キー（枠1＝setId／枠2・3＝{setId}_s2/_s3）
      const exportView = await prepareTripExport(uid, set.id, slotKey);
      const payload = buildExportPayload(exportView, master, localDateStr());
      const res = await fetch('/api/export-trip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      let data = null;
      const ct = res.headers.get('content-type') || '';
      if (ct.includes('application/json')) {
        data = await res.json();
      }
      if (!res.ok || !data || !data.ok) {
        const msg = data && data.error ? data.error : `記録に失敗しました（HTTP ${res.status}）`;
        throw new Error(msg);
      }
      await markTripExported(uid, set.id, localDateTimeStr(), slotKey);
      window.dispatchEvent(new Event('taskflow:budget-updated'));
      setExportModal(false);
      vibrate([30, 50, 30]);
      // 記録成功→そのまま費用データのリセットを確認（社長指示 2026-07-07）
      setResetAsk({ afterExport: true });
    } catch (err) {
      console.error('Export failed:', err);
      setExportError(err.message || '記録に失敗しました。通信状態を確認してください。');
    } finally {
      setExporting(false);
    }
  };

  const exported = !!(trip && trip.status === 'done');
  const kind = trip && trip.kind === 'business' ? 'business' : 'private';

  // ===== 部分描画：旅行名・区分・旅行日（予算設定パネル／⚙️旅行の設定で共用） =====
  // 旅行名（v1.9.3）：持ち物セット名はテンプレ（宿泊・日帰り等）として使い回すため、
  // 旅行固有の名前を別途つけられるようにする。未設定ならセット名で記録（後方互換）
  const renderKindAndDates = () => (
    <>
      <div className="tm-field-group">
        <p className="tm-field-label">旅行名</p>
        <input
          type="text"
          className="cl-modal-input tm-tripname-input"
          placeholder="例：北海道オフ会"
          value={tripNameDraft !== null ? tripNameDraft : (trip && trip.tripName) || ''}
          onChange={(e) => setTripNameDraft(e.target.value)}
          onBlur={commitTripName}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.target.blur();
          }}
          maxLength={50}
          aria-label="旅行名"
        />
        <p className="tm-field-note">未入力ならリスト名「{set.name}」で記録されます</p>
      </div>

      <div className="tm-field-group">
        <p className="tm-field-label">区分</p>
        <div className="tm-kind-toggle" role="radiogroup" aria-label="区分">
          <button
            type="button"
            className={`tm-kind-btn tm-kind-btn-private ${kind === 'private' ? 'active' : ''}`}
            onClick={() => writeTripMeta({ kind: 'private' })}
          >
            🏠 private
          </button>
          <button
            type="button"
            className={`tm-kind-btn tm-kind-btn-business ${kind === 'business' ? 'active' : ''}`}
            onClick={() => writeTripMeta({ kind: 'business' })}
          >
            💼 事業
          </button>
        </div>
      </div>

      <div className="tm-field-group">
        <p className="tm-field-label">旅行日</p>
        {/* 期間カレンダー（v1.12.3）。開始→終了を続けて選べて、間が帯でつながる。
            見出しの帯そのものが開閉ボタン＝開くときも閉じるときも同じ場所を押せばよい */}
        <button
          type="button"
          className={`tm-date-summary app-toggle${calendarOpen ? ' is-open' : ''}`}
          onClick={() => setCalendarOpen((v) => !v)}
          aria-expanded={calendarOpen}
        >
          <span className="tm-date-summary-label" aria-hidden="true">📅</span>
          <span className="tm-date-summary-value">
            {trip && trip.startDate
              ? `${trip.startDate}${trip.endDate ? ` 〜 ${trip.endDate}` : ' 〜 （終了日を選ぶ）'}`
              : '未設定'}
          </span>
          <span className="app-toggle-caret" aria-hidden="true">{calendarOpen ? '▲' : '▼'}</span>
        </button>
        {calendarOpen && (
          <DateRangeCalendar
            startDate={(trip && trip.startDate) || null}
            endDate={(trip && trip.endDate) || null}
            onChange={handleRangeChange}
          />
        )}
      </div>
    </>
  );

  // 予算金額の入力欄（設定パネル／費目パネルで共用）
  const renderBudgetInput = (row) => (
    <div className="tm-budget-amount">
      <span className="tm-budget-yen">¥</span>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        className="tm-budget-input"
        value={budgetDraft[row.catId] !== undefined ? budgetDraft[row.catId] : row.amount || ''}
        placeholder="0"
        onChange={(e) =>
          setBudgetDraft((prev) => ({
            ...prev,
            [row.catId]: e.target.value.replace(/[^0-9]/g, '').slice(0, 8),
          }))
        }
        onBlur={() => commitBudget(row.catId)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.target.blur();
        }}
      />
    </div>
  );

  // ===== 描画 =====
  return (
    <div className="tm-view">
      {/* ヘッダー：戻る＋旅行名（未設定ならセット名・v1.9.3） */}
      <div className="tm-header">
        <button type="button" className="tm-back-btn" onClick={onBack}>
          ← 持ち物へ戻る
        </button>
        <span className="tm-set-name">{tripDisplayName(view)}</span>
        {trip && (
          <span className={`tm-kind-badge tm-kind-${kind}`}>
            {kind === 'business' ? '事業' : 'private'}
          </span>
        )}
        {/* 設定はこの歯車1つに集約（v1.12.1）。以前は画面下部の折りたたみに散っていた */}
        {trip && (
          <button
            type="button"
            className="tm-gear-btn"
            onClick={() => setSettingsOpen(true)}
            aria-label="旅行の設定を開く"
            title="旅行の設定"
          >
            ⚙️
          </button>
        )}
      </div>

      {/* プラン切替チップ（v1.15.0：上限なし・➕で追加・🗑️で削除）。
          データのある枠だけを、旅行日が近い順に左から並べる（過ぎた旅行は薄く右側・日付未入力は右端）。
          選択中の枠が空（作りたて）のときだけ破線チップで表示。並びきらなければ横スクロール */}
      <div className="tm-slot-bar">
        <div className="tm-slot-tabs" role="tablist" aria-label="プランの切り替え">
          {slotKeys.map((k) => {
            const used = slotHasData(set, k);
            const status = slotStatus(set, k, today);
            const t = getTrip(getSlotView(set, k));
            const dateText = t && t.startDate ? String(t.startDate).slice(5).replace('-', '/') : '';
            return (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={slotKey === k}
                className={`tm-slot-tab ${slotKey === k ? 'active' : ''} ${used ? '' : 'is-empty'} ${status === 'past' ? 'is-past' : ''}`}
                onClick={() => onSlotChange && onSlotChange(k)}
                title={status === 'past' ? '終わった旅行' : undefined}
              >
                <span className="tm-slot-name">{used ? slotLabel(set, k) : '新しいプラン'}</span>
                {dateText && (
                  <span className="tm-slot-date">
                    {status === 'past' ? '済 ' : ''}
                    {dateText}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="tm-slot-tool-btn"
          onClick={handleAddPlan}
          aria-label="プランを追加"
          title="プランを追加"
        >
          ＋
        </button>
        {slotHasData(set, slotKey) && (
          <button
            type="button"
            className="tm-slot-tool-btn tm-slot-del"
            onClick={() => setResetAsk({ afterExport: false, deletePlan: true })}
            aria-label="このプランを削除"
            title="このプランを削除"
          >
            🗑️
          </button>
        )}
      </div>

      {planSyncError && (
        <div role="alert" className="tm-inline-error">
          {planSyncError}
          <button type="button" onClick={() => setPlanSyncRetry(n => n + 1)}>再試行</button>
        </div>
      )}

      {/* ===== テーマ（v1.11.0）：旅行の方向性。旅行中に常に目に入る最上部に固定表示 ===== */}
      {hasTheme ? (
        // 複数行は「／」でつないで1行にまとめる（v1.12.1）。行数ぶん縦に伸びて
        // 画面がゴチャつくのを避けるため。全文はタップして編集モーダルで読める。
        <button type="button" className="tm-theme-card tm-tappable" onClick={openThemeModal} aria-label="テーマを編集">
          {mainThemeLines.length > 0 && (
            <span className="tm-theme-main">🎯 {mainThemeLines.join(' ／ ')}</span>
          )}
          {subThemeLines.length > 0 && (
            <span className="tm-theme-sub">🧭 {subThemeLines.join(' ／ ')}</span>
          )}
          <span className="tm-theme-edit" aria-hidden="true">✏️</span>
        </button>
      ) : (
        <button type="button" className="tm-theme-cta tm-tappable" onClick={openThemeModal}>
          🎯 テーマを決める（この旅行の方向性）
        </button>
      )}

      {/* ===== 予実差サマリー（常時最上段・固定） =====
          オフ会予算はこの緑枠の左上に同居させる（v1.12.2 社長指示）。
          プランの予算とオフ会の枠を1か所で見比べられるようにするため。 */}
      <div className="tm-summary" aria-live="polite">
        <div className="tm-summary-meetup">
          <MeetupBudgetCard compact={planReady && !setupOpen} contextDate={trip?.startDate} />
        </div>
        <div className="tm-summary-remain">
          <span className="tm-summary-remain-label">
            {overall.remaining >= 0 ? 'のこり' : 'オーバー'}
          </span>
          <span className={`tm-summary-remain-value ${overall.remaining < 0 ? 'over' : ''}`}>
            {overall.remaining >= 0
              ? `${formatYen(overall.remaining)} ✅`
              : `🔴 ${formatYen(Math.abs(overall.remaining))}`}
          </span>
        </div>
        <div className="tm-bar">
          <div
            className={`tm-bar-fill tm-bar-${barState(overall.budgetTotal, overall.actualTotal)}`}
            style={{ width: `${barPct(overall.budgetTotal, overall.actualTotal)}%` }}
          />
        </div>
        <div className="tm-summary-sub">
          <span>実費 {formatYen(overall.actualTotal)}</span>
          <span>予算 {formatYen(overall.budgetTotal)}</span>
        </div>
      </div>

      {/* ===== 状態A：予算未決定 → 「予算を決める」だけ ===== */}
      {!planReady && !setupOpen && (
        <>
          <button type="button" className="tm-plan-cta" onClick={() => setSetupOpen(true)}>
            💰 予算を決める
          </button>
          <p className="tm-plan-note">まずは予算から。決めると実費の記録がここに出ます</p>
        </>
      )}

      {/* ===== 予算設定パネル（状態Aから開く） ===== */}
      {setupOpen && (
        <div className="tm-setup">
          {renderKindAndDates()}

          <div className="tm-field-group">
            <p className="tm-field-label">費目ごとの予算</p>
            {budgetRows.map((row) => (
              <div key={row.catId} className={`tm-budget-row tm-colorline-${row.color}`}>
                <span className="tm-budget-cat">
                  {row.icon} {row.name}
                </span>
                {renderBudgetInput(row)}
              </div>
            ))}
            <div className="tm-budget-total">
              合計 <strong>{formatYen(draftTotal)}</strong>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-accent tm-setup-start"
            onClick={handleStartWithBudget}
            disabled={draftTotal <= 0}
          >
            ✔ この予算でスタート
          </button>

          <button type="button" className="recur-open-btn" onClick={openMasterModal}>
            <span className="recur-open-icon" aria-hidden="true">🏷️</span>
            費目マスタを編集（追加・変更・削除）
          </button>
        </div>
      )}

      {/* ===== 状態B：予算決定後（タブなし1画面） ===== */}
      {planReady && !setupOpen && (
        <>
          <p className="tm-cat-hint">費目の行をタップすると電卓が開きます ／ ✏️で予算の修正と明細</p>
          <div className="tm-rows">
            {rows.map((row) => {
              const state = barState(row.budget, row.actual);
              const isEditing = editCatId === row.catId;
              const catExpenses = expenses
                .filter((e) => e.catId === row.catId)
                .slice()
                .sort((a, b) => (a.at < b.at ? 1 : -1));
              return (
                <div key={row.catId} className={`tm-row-wrap tm-colorline-${row.color}`}>
                  <div className="tm-row-main">
                    <button
                      type="button"
                      className="tm-row-btn tm-tappable"
                      onClick={() => handleRowTap(row)}
                      aria-haspopup="dialog"
                      aria-label={`${row.name}の実費を記録（電卓を開く）`}
                    >
                      <div className="tm-row-top">
                        <span className="tm-row-name">
                          {row.icon} {row.name}
                        </span>
                        <span className="tm-row-nums">
                          {formatYen(row.actual)}/{formatYen(row.budget)}
                        </span>
                      </div>
                      <div className="tm-row-bottom">
                        <div className="tm-bar">
                          <div
                            className={`tm-bar-fill tm-bar-${state}`}
                            style={{ width: `${barPct(row.budget, row.actual)}%` }}
                          />
                        </div>
                        <span className={`tm-row-remain ${state === 'over' ? 'over' : ''}`}>
                          {row.remaining >= 0
                            ? `残 ${formatYen(row.remaining)}${state === 'warn' ? ' ⚠️' : ''}`
                            : `🔴 ${formatYen(Math.abs(row.remaining))} 超`}
                        </span>
                      </div>
                    </button>
                    <button
                      type="button"
                      className={`tm-row-edit tm-toggle${isEditing ? " is-open" : ""}`}
                      onClick={() => setEditCatId(isEditing ? null : row.catId)}
                      aria-expanded={isEditing}
                      aria-label={`${row.name}の予算修正と明細`}
                    >
                      ✏️
                    </button>
                  </div>
                  {isEditing && (
                    <div className="tm-row-editor">
                      <div className="tm-row-editor-budget">
                        <span className="tm-field-label">この費目の予算</span>
                        {renderBudgetInput(row)}
                      </div>
                      {catExpenses.length === 0 && <p className="tm-empty-note">まだ入力がありません</p>}
                      {catExpenses.map((e) => (
                        <div key={e.id} className="tm-today-row">
                          <span className="tm-today-time">{e.at.slice(5, 16)}</span>
                          <span className="tm-today-amount">{formatYen(e.amount)}</span>
                          <span className="tm-today-memo">{e.memo}</span>
                          <button
                            type="button"
                            className="tm-today-del"
                            onClick={() => {
                              if (window.confirm(`${formatYen(e.amount)} の記録を削除しますか？`)) {
                                handleRemoveExpense(e);
                              }
                            }}
                            aria-label="この記録を削除"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {todayExpenses.length > 0 && (
            <div className="tm-today">
              <p className="tm-today-title">きょうの入力</p>
              {todayExpenses.map((e) => {
                const cat = rows.find((r) => r.catId === e.catId);
                return (
                  <div key={e.id} className="tm-today-row">
                    <span className="tm-today-icon">{cat ? cat.icon : '❓'}</span>
                    <span className="tm-today-amount">{formatYen(e.amount)}</span>
                    <span className="tm-today-memo">{e.memo}</span>
                    <span className="tm-today-time">{e.at.slice(11)}</span>
                    <button
                      type="button"
                      className="tm-today-del"
                      onClick={() => handleRemoveExpense(e)}
                      aria-label="この入力を取り消す"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* 設定はヘッダーの⚙️に集約（v1.12.1）。ここにあった折りたたみは廃止 */}

          {/* 完了（エクスポート）ボタン：最下部＝誤タップしにくい位置。確認モーダル付き */}
          <button
            type="button"
            className={`btn tm-export-btn ${exported ? 'btn-secondary' : 'btn-accent'}`}
            onClick={() => {
              setExportError('');
              setExportModal(true);
            }}
          >
            {exported ? '✔ 記録済み（再記録）' : '✅ 旅行を完了してスプシへ記録'}
          </button>
          {exported && trip.exportedAt && (
            <p className="tm-exported-note">前回の記録：{trip.exportedAt}</p>
          )}
        </>
      )}

      {/* トースト */}
      {toast && (
        <div className="undo-toast tm-toast" role="status">
          <span className="undo-toast-text">{toast}</span>
        </div>
      )}

      {/* ===== ポップアップ電卓（費目行タップで開く・v1.9.2） ===== */}
      {calcRow && (
        <div className="modal-overlay" onClick={closeCalc}>
          <div className="modal-content cl-modal tm-calc-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={`${calcRow.name}の実費を記録`}>
            <div className="modal-header">
              <h2 className={`tm-calc-title tm-colorline-${calcRow.color}`}>
                {calcRow.icon} {calcRow.name}
              </h2>
              <button className="modal-close" onClick={closeCalc} aria-label="閉じる">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p className="tm-calc-sub">
                いま {formatYen(calcRow.actual)} / 予算 {formatYen(calcRow.budget)}
                {calcRow.budget > 0 && `（残 ${formatYen(calcRow.remaining)}）`}
              </p>
              <div className="tm-calc-expr" aria-hidden="true">{calcExprText(calcState) || ' '}</div>
              <div className="tm-amount-display" aria-live="polite">
                <span className="tm-amount-yen">¥</span>
                <span className="tm-amount-value">
                  {calcAmount > 0 ? formatYen(calcAmount).slice(1) : '0'}
                </span>
              </div>
              <div className="tm-calc-keypad">
                {CALC_KEYS.map((k) => (
                  <button
                    key={k}
                    type="button"
                    className={`tm-key ${k === 'back' ? 'tm-key-back' : ''} ${['+', '-', '*', '/'].includes(k) ? 'tm-key-op' : ''}`}
                    onClick={() => setCalcState((prev) => applyCalcKey(prev, k))}
                    aria-label={k === 'back' ? '1文字削除' : CALC_KEY_LABEL[k] || k}
                  >
                    {CALC_KEY_LABEL[k] || k}
                  </button>
                ))}
                <button
                  type="button"
                  className="tm-key tm-key-back"
                  onClick={() => setCalcState((prev) => applyCalcKey(prev, 'clear'))}
                  aria-label="全部消す"
                >
                  C
                </button>
                <button
                  type="button"
                  className="tm-key tm-key-eq"
                  onClick={() => setCalcState((prev) => applyCalcKey(prev, '='))}
                  aria-label="計算する"
                >
                  ＝
                </button>
              </div>
              <input
                type="text"
                className="tm-memo-input"
                placeholder="メモ（任意）"
                value={calcMemo}
                onChange={(e) => setCalcMemo(e.target.value)}
                maxLength={60}
              />
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeCalc}>
                  キャンセル
                </button>
                <button
                  type="button"
                  className="btn btn-accent tm-calc-record"
                  onClick={handleCalcRecord}
                  disabled={calcAmount <= 0}
                >
                  {calcAmount > 0 ? `${formatYen(calcAmount)} を記録する` : '記録する'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== 旅行の設定モーダル（v1.12.1・ヘッダーの⚙️から） =====
          旅行名・区分・日付・テーマ・費目マスタ・リセットをここ1か所に集約した。
          以前は画面上部（テーマ）と下部（折りたたみ）に分かれていて分かりにくかったため。 */}
      {settingsOpen && (
        <div className="modal-overlay" onClick={() => setSettingsOpen(false)}>
          <div className="modal-content cl-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="旅行の設定">
            <div className="modal-header">
              <h2>⚙️ 旅行の設定</h2>
              <button className="modal-close" onClick={() => setSettingsOpen(false)} aria-label="閉じる">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              {renderKindAndDates()}
              <button
                type="button"
                className="recur-open-btn"
                onClick={() => { setSettingsOpen(false); openThemeModal(); }}
              >
                <span className="recur-open-icon" aria-hidden="true">🎯</span>
                テーマを編集（メイン・サブ）
              </button>
              <button
                type="button"
                className="recur-open-btn"
                onClick={() => { setSettingsOpen(false); openMasterModal(); }}
              >
                <span className="recur-open-icon" aria-hidden="true">🏷️</span>
                費目マスタを編集（追加・変更・削除）
              </button>
              <button
                type="button"
                className="recur-open-btn tm-reset-open-btn"
                onClick={() => { setSettingsOpen(false); setResetAsk({ afterExport: false }); }}
              >
                <span className="recur-open-icon" aria-hidden="true">🗑️</span>
                費用データをリセット（持ち物リストは残ります）
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== テーマ編集モーダル（v1.11.0） ===== */}
      {themeDraft && (
        <div className="modal-overlay" onClick={() => setThemeDraft(null)}>
          <div className="modal-content cl-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="テーマの編集">
            <div className="modal-header">
              <h2>🎯 この旅行のテーマ</h2>
              <button className="modal-close" onClick={() => setThemeDraft(null)} aria-label="閉じる">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p className="cl-modal-hint">
                旅行の方向性を決めておくと、費用画面のいちばん上に表示されて、旅行中いつでも目に入ります。
                <strong>1行に1つ</strong>書けます。
              </p>
              <div className="tm-field-group">
                <p className="tm-field-label">🎯 メインテーマ（この旅行の目的）</p>
                <textarea
                  className="tm-theme-textarea"
                  placeholder={'例：\n〇〇について3人以上の先輩に意見をもらう\n〇〇さんと顔見知りになる'}
                  value={themeDraft.main}
                  onChange={(e) => setThemeDraft((prev) => ({ ...prev, main: e.target.value }))}
                  maxLength={500}
                  rows={3}
                  aria-label="メインテーマ"
                />
              </div>
              <div className="tm-field-group">
                <p className="tm-field-label">🧭 サブテーマ（過ごし方の方針）</p>
                <textarea
                  className="tm-theme-textarea"
                  placeholder={'例：\n予定を入れすぎずのんびり過ごす\nなるべく安く済ませる'}
                  value={themeDraft.sub}
                  onChange={(e) => setThemeDraft((prev) => ({ ...prev, sub: e.target.value }))}
                  maxLength={500}
                  rows={3}
                  aria-label="サブテーマ"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setThemeDraft(null)}>
                  キャンセル
                </button>
                <button type="button" className="btn btn-accent" onClick={saveThemes}>
                  ✔ 保存
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== 費用データリセット確認モーダル（記録直後／⚙️設定から） ===== */}
      {resetAsk && (
        <div className="modal-overlay" onClick={() => !resetting && setResetAsk(null)}>
          <div className="modal-content cl-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="費用データのリセット確認">
            <div className="modal-header">
              <h2>
                {resetAsk.afterExport
                  ? '✅ 記録しました'
                  : resetAsk.deletePlan
                    ? `🗑️ プラン「${slotLabel(set, slotKey)}」を削除`
                    : '🗑️ 費用データをリセット'}
              </h2>
              <button
                className="modal-close"
                onClick={() => !resetting && setResetAsk(null)}
                aria-label="閉じる"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              {resetAsk.afterExport ? (
                <p className="cl-modal-hint">
                  スプレッドシートへの記録が完了しました。
                  <br />
                  この旅行の費用データ（予算・実費）をリセットしますか？
                  <br />
                  <strong>持ち物リストはそのまま残ります。</strong>
                </p>
              ) : (
                <p className="cl-modal-hint">
                  {resetAsk.deletePlan
                    ? 'このプランの旅行データ（旅行名・区分・旅行日・テーマ・予算・実費）をすべて消して、一覧から外します。'
                    : 'この旅行の費用データ（区分・旅行日・予算・実費）をすべて消して、最初の状態に戻します。'}
                  <strong>持ち物リストはそのまま残ります。</strong>
                  <br />
                  {exported && trip && trip.exportedAt
                    ? `この旅行はスプレッドシートに記録済みです（前回：${trip.exportedAt}）。スプシ側の行は消えません。`
                    : trip?.recordId
                      ? '未確定の予定は取消扱いにし、予定込み残の計算から外します。実績はまだ未記録です。費用データは元に戻せません。'
                      : '⚠️ この旅行はまだスプレッドシートに記録されていません。リセットすると費用データは元に戻せません。'}
                </p>
              )}
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setResetAsk(null)}
                  disabled={resetting}
                >
                  {resetAsk.afterExport ? 'あとで' : 'キャンセル'}
                </button>
                <button
                  type="button"
                  className={`btn ${resetAsk.afterExport ? 'btn-accent' : 'tm-reset-danger'}`}
                  onClick={handleResetExecute}
                  disabled={resetting}
                >
                  {resetting
                    ? resetAsk.deletePlan ? '削除中...' : 'リセット中...'
                    : resetAsk.deletePlan ? '削除する' : 'リセットする'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== 費目マスタ編集モーダル ===== */}
      {masterModal && (
        <div className="modal-overlay" onClick={() => setMasterModal(false)}>
          <div className="modal-content cl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🏷️ 費目マスタの編集</h2>
              <button className="modal-close" onClick={() => setMasterModal(false)} aria-label="閉じる">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p className="cl-modal-hint">
                名前・アイコン・色を変えた内容は<strong>これから作る予算</strong>に反映されます（過去の旅行の記録はそのまま残ります）。
                <br />
                <strong>▲▼で並び順</strong>を変えると、予算・実費・スプレッドシートの<strong>並びがすべて入れ替わります</strong>（宿泊・日帰り・プラン1〜3すべて共通）。
              </p>
              <div className="tm-master-list">
                {master.map((c, i) => (
                  <div key={c.id} className={`tm-master-row ${catForm.id === c.id ? 'editing' : ''}`}>
                    <span className="tm-master-order">
                      <button
                        type="button"
                        className="tm-order-btn"
                        onClick={() => handleCatMove(i, -1)}
                        disabled={i === 0}
                        aria-label={`${c.name}を上へ移動`}
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        className="tm-order-btn"
                        onClick={() => handleCatMove(i, 1)}
                        disabled={i === master.length - 1}
                        aria-label={`${c.name}を下へ移動`}
                      >
                        ▼
                      </button>
                    </span>
                    <span className={`tm-color-dot tm-dot-${c.color === 'red' || c.color === 'blue' ? c.color : 'white'}`} aria-hidden="true" />
                    <span className="tm-master-label">
                      {c.icon} {c.name}
                    </span>
                    <button
                      type="button"
                      className="cl-cat-btn"
                      onClick={() => {
                        setCatForm({ id: c.id, name: c.name, icon: c.icon || EXPENSE_ICONS[0], color: c.color === 'red' || c.color === 'blue' ? c.color : 'white' });
                        setCatError('');
                      }}
                      aria-label={`${c.name}を編集`}
                    >
                      ✏️
                    </button>
                    <button
                      type="button"
                      className="cl-cat-btn cl-cat-btn-danger"
                      onClick={() => handleCatDelete(c)}
                      aria-label={`${c.name}を削除`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <form onSubmit={handleCatSubmit} className="tm-master-form">
                <p className="tm-field-label">{catForm.id ? '費目を変更' : '費目を追加'}</p>
                <input
                  type="text"
                  className="cl-modal-input"
                  placeholder="費目名（例：交通費）"
                  value={catForm.name}
                  onChange={(e) => setCatForm((prev) => ({ ...prev, name: e.target.value }))}
                  maxLength={20}
                />
                <div className="cl-icon-picker" role="radiogroup" aria-label="アイコンを選択">
                  {EXPENSE_ICONS.map((ic) => (
                    <button
                      key={ic}
                      type="button"
                      className={`cl-icon-choice ${catForm.icon === ic ? 'active' : ''}`}
                      onClick={() => setCatForm((prev) => ({ ...prev, icon: ic }))}
                      aria-label={`アイコン ${ic}`}
                    >
                      {ic}
                    </button>
                  ))}
                </div>
                <div className="tm-color-picker" role="radiogroup" aria-label="色を選択（赤=事業寄り・青=private寄り・白=両方）">
                  {COLOR_CHOICES.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      className={`tm-color-choice ${catForm.color === c.value ? 'active' : ''}`}
                      onClick={() => setCatForm((prev) => ({ ...prev, color: c.value }))}
                    >
                      <span className={`tm-color-dot tm-dot-${c.value}`} aria-hidden="true" />
                      {c.label}
                    </button>
                  ))}
                </div>
                {catError && <p className="error-text">{catError}</p>}
                <div className="modal-actions">
                  {catForm.id && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setCatForm({ id: null, name: '', icon: EXPENSE_ICONS[0], color: 'white' })}
                    >
                      編集をやめる
                    </button>
                  )}
                  <button type="submit" className="btn btn-accent" disabled={!catForm.name.trim()}>
                    {catForm.id ? '変更' : '＋ 追加'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ===== エクスポート確認モーダル ===== */}
      {exportModal && (
        <div className="modal-overlay" onClick={() => !exporting && setExportModal(false)}>
          <div className="modal-content cl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✅ スプレッドシートへ記録</h2>
              <button
                className="modal-close"
                onClick={() => !exporting && setExportModal(false)}
                aria-label="閉じる"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="tm-export-preview">
                <div className="tm-export-line">
                  <span>旅行</span>
                  <strong>{tripDisplayName(view)}</strong>
                </div>
                <div className="tm-export-line">
                  <span>区分</span>
                  <strong>{kind === 'business' ? '💼 事業' : '🏠 private'}</strong>
                </div>
                <div className="tm-export-line">
                  <span>期間</span>
                  <strong>
                    {trip && trip.startDate ? trip.startDate : '未設定'}
                    {trip && trip.endDate ? ` 〜 ${trip.endDate}` : ''}
                  </strong>
                </div>
                <div className="tm-export-line">
                  <span>実費総額</span>
                  <strong>{formatYen(overall.actualTotal)}</strong>
                </div>
                <div className="tm-export-line">
                  <span>予算総額</span>
                  <strong>{formatYen(overall.budgetTotal)}</strong>
                </div>
              </div>
              <p className="cl-modal-hint">
                スプレッドシート「旅行費用管理」の{kind === 'business' ? '「事業」' : '「private」'}タブに記録します。
                {exported && ' すでに記録済みの行は新しい内容で書き直されます（二重登録はされません）。'}
              </p>
              {exportError && <p className="error-text">{exportError}</p>}
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setExportModal(false)}
                  disabled={exporting}
                >
                  キャンセル
                </button>
                <button type="button" className="btn btn-accent" onClick={handleExport} disabled={exporting}>
                  {exporting ? '記録中...' : exported ? '再記録する' : '記録する'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
